/* eslint-disable max-lines */
import endsWith from "lodash.endswith";
import { CURRENCY_ID, INDIAN_RUPEE } from "../../constants/common/common";
import { cleverTapCommonWords } from "../../analytics/clevertapConstants";
import { productTypes } from "../../constants/pageConstants/pdpConstants";
import { GET_IS_SAME_DAY_CATALOG } from "../../api/common/constant";
import { fetchContentFromCMSOnClient } from "../../api/common";
import { ROUTE_CONSTANTS } from "../../constants/route";
import {
  LAST_VISITED_ACCURACY_LEVEL,
  LAST_VISITED_ACCURACY_TYPE,
  LAST_VISITED_ADDRESS_LAT_LONG,
  LAST_VISITED_LAT_LONG,
  LAST_VISITED_PLACE_TYPES,
} from "../../constants/storage";
/**
 * Description placeholder
 * @date 04/04/2024 - 15:18:39
 *
 * @export
 * @returns {*}
 */
export default function isMobile() {
  let isMobileDevice = false;
  if (typeof window !== "undefined") {
    isMobileDevice = window?.matchMedia("(max-width: 1024px)")?.matches;
  }
  return isMobileDevice;
}

/**
 * Get the initials from a given name.
 * @param {string} name - The name to get initials from.
 * @returns {string} - Initials of the name.
 */
export const getFirstLetters = (name) => {
  const words = name?.split(" ");
  let initials = "";
  words?.forEach((word) => {
    initials += word.charAt(0);
  });
  return initials;
};

/**
 * Builds partner membership flag fields for analytics payloads.
 * Example: { indigo: true, airIndia: false } => { is_indigo_member: true, is_airIndia_member: false }
 * @param {Record<string, unknown>} partner - Partner membership object.
 * @returns {Record<string, boolean>} Flag map keyed by `is_<key>_member`.
 */
export const buildPartnerMembershipFlags = (partner) => {
  if (!partner || typeof partner !== "object") {
    return null;
  }

  const partnerMembershipFlags = Object.entries(partner).reduce((acc, [key, value]) => {
    acc[`is_${key}_member`] = Boolean(value);
    return acc;
  }, {});

  return Object.keys(partnerMembershipFlags).length ? partnerMembershipFlags : null;
};

/**
 * Checks whether the cart should be cleared because the selected pincode
 * differs from the last saved pincode.
 * @param {number} cartCount - Number of items currently in the cart.
 * @param {string|number} lastSavedPincode - Pincode last saved against the cart.
 * @param {string|number} pincode - Newly selected pincode.
 * @returns {boolean} - True if cart has items, a last saved pincode exists, and it differs from the selected pincode.
 */
export const hasPincodeChangedWithCartItems = (cartCount, lastSavedPincode, pincode) => {
  if (!(cartCount > 0)) return false;
  if (lastSavedPincode === null || lastSavedPincode === undefined) return false;
  return String(lastSavedPincode) !== String(pincode);
};

/**
 * Throttle function to reduce the multiple api calls.
 * @param {*} func
 * @param {*} delay
 * @returns
 */
export function throttle(func, delay) {
  let timeout = null;
  return function (...args) {
    if (!timeout) {
      timeout = setTimeout(() => {
        func(...args);
        timeout = null;
      }, delay);
    }
  };
}

/**
 * Splits a string by commas and returns the part at the specified index.
 *
 * @param {string} value - The string to split.
 * @param {number} index - The index of the part to return.
 * @returns {string} - The part of the string at the specified index, or an empty string if the index is out of bounds.
 */
export const stringWithCommaSplitter = (value, index) => {
  if (value !== undefined) {
    const parts = value?.split(", ");
    if (index >= 0 && index < parts?.length) {
      return parts[index];
    } else {
      return value;
    }
  }
};

/** To check if passed string is a json string
 *
 * @param {string} str json string
 *
 * @returns {boolean} true or false depending upon if string can be parsed
 */
export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * This method provides a scroll behavior on click to the section present in the Dome
 *
 * @param {string} scrollId - div Id
 * @param {number} offsetValue - offset value
 */
export const scrollToSection = (scrollId, offsetValue) => {
  const supportsNativeSmoothScroll = "scrollBehavior" in document.documentElement.style;
  if (document.getElementById(scrollId)) {
    const eleOffsetTop = document.getElementById(scrollId).offsetTop - offsetValue;
    if (supportsNativeSmoothScroll) {
      window.scroll({ top: eleOffsetTop, behavior: "instant" });
    }
  }
};

/**
 * Formats a numeric suffix with one decimal place, trimming trailing ".0".
 * @param {number} value - Scaled numeric value
 * @param {string} suffix - Unit suffix (e.g. "K", "L")
 * @returns {string}
 */
const formatWithCompactSuffix = (value, suffix) => {
  const fixed = value.toFixed(1);
  const trimmed = fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
  return `${trimmed}${suffix}`;
};

/**
 * Formats review/count numbers for compact display.
 * - Below 1,000: full number (e.g. 856)
 * - 1,000 – 99,999: K format (e.g. 8.4K)
 * - 1,00,000 and above: Lakh format (e.g. 1.5L)
 *
 * @param {number} count - The count to format
 * @returns {string} Formatted count string
 */
export const formatReviewCount = (count) => {
  const num = Number(count);

  if (Number.isNaN(num)) {
    return "";
  }

  if (num < 1000) {
    return num.toString();
  }

  if (num < 100000) {
    return formatWithCompactSuffix(num / 1000, "K");
  }

  return formatWithCompactSuffix(num / 100000, "L");
};

/**
 * Converts overallRating (1–10) to a 5-star rating (1–5).
 * Clamps input to 1–10, divides by 2, then clamps output to 1–5.
 * @param {number} overallRating - Rating from API on a 1–10 scale.
 * @returns {number} Rating clamped to 1–5 for stars/schema.
 */
export const getFiveStarRating = (overallRating) => {
  const parsed = Number(overallRating);
  const safeRating = Number.isFinite(parsed) ? parsed : 1;
  const clampedTenScale = Math.min(10, Math.max(1, safeRating));
  const fiveStarScale = clampedTenScale / 2;
  return Math.min(5, Math.max(1, fiveStarScale));
};

/**
 * This method formats a given number into a shortened form using "K"
 * for thousands, appending "Reviews" to the end of the result.
 *
 * @param {number} number - The number to format
 * @returns {string} - The formatted string in "K" format if the number is
 * greater than or equal to 1000, otherwise returns the number followed by "Reviews"
 */
export const formatToK = (number, isReviewTextHide) => {
  const formatted = formatReviewCount(number);

  if (number >= 1000) {
    return formatted;
  }

  return `${formatted}${isReviewTextHide ? "" : " Reviews"}`;
};

/**
 * Debounce function to delay the execution of a given function until after a specified wait time.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {Function} - A debounced version of the given function.
 */
export const debounce = (func, wait) => {
  let timeout;

  /**
   *  debounced
   * @param  {...any} args
   */
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };

  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
};

/**
 * Builds the desktop product-card image list.
 * The response URL is already the medium image for the first photo, so it is used unchanged.
 * Remaining images use the large path and start at `_2`, because the medium URL stands in for `_1`.
 *
 * @param {string} url Product image URL from the response.
 * @param {number} imageCount Total number of images for the product.
 * @returns {string[]} Medium first image, then large images from `_2` onward.
 */
export const getProductLargeImgList = (url, imageCount) => {
  if (!url) return ["/assets/images/image_placeholder-bb.png"];
  if (!imageCount || imageCount <= 1) return [url];

  let largeImageUrl = url.replace("/m/", "/l/").split(".jpg")[0];
  if (endsWith(largeImageUrl, "_1")) {
    largeImageUrl = largeImageUrl.slice(0, -2);
  }

  const productImages = [url];
  for (let index = 2; index <= imageCount; index += 1) {
    productImages.push(`${largeImageUrl}_${index}.jpg`);
  }

  return productImages;
};

/**
 * Function to filter out null, undefined, and empty string values from an object
 *
 * @param {object} obj contains object to be filtered
 * @returns {object} filtered object
 */
export const removeEmptyNullAndUndefinedFromObject = (obj) => {
  return Object.fromEntries(
    // eslint-disable-next-line no-unused-vars
    Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined && value !== ""),
  );
};

/**
 * Convert the filter objects in to array.
 * @param {*} filterObj
 * @returns
 */
export const convertFilterObjToArray = (filterObj) => {
  const manipulatedArr = Object?.keys(filterObj)?.length
    ? Object?.values(filterObj)?.reduce((acc, item) => {
        if (item) {
          return [...acc, item];
        }
        return acc;
      }, [])
    : [];

  return manipulatedArr;
};

/** */
export const updateCurrency = (
  selectedCurrency = {},
  price,
  isShowFree = false,
  isShowFreePrice = false,
  isForSchema = true,
  isPriceProp = true,
) => {
  let updatedPrice = price?.toLocaleString("en-IN");
  let currencySymbol = INDIAN_RUPEE;
  if (
    price >= 0 &&
    selectedCurrency?.currency?.id &&
    selectedCurrency?.currency?.factor &&
    selectedCurrency?.currency?.id !== CURRENCY_ID
  ) {
    updatedPrice = (selectedCurrency?.currency?.factor * parseFloat(price)).toFixed(2);
    currencySymbol = selectedCurrency?.currency?.id;
  }
  return isForSchema ? (
    <>
      {isPriceProp && (
        <span itemProp="price" className="hidden">
          {price}
        </span>
      )}
      {price === 0 && isShowFree ? (
        <>
          {isShowFreePrice && <span className="line-through">{currencySymbol}29</span>}
          <span className="font-600 text-offer-900"> FREE</span>
        </>
      ) : (
        <>
          {currencySymbol} <span className="-ml-3">{updatedPrice}</span>
        </>
      )}
    </>
  ) : (
    `${currencySymbol} ${updatedPrice}`
  );
};

/**
 * Parses a metadata string to extract title, description, keywords, canonical URL, Twitter meta tags, OG meta tags, and relAlt tags.
 * Handles both string and object inputs (for batch API compatibility).
 *
 * @param {string|Object} metaString - The raw metadata string or object.
 * @returns {Object} An object containing title, description, keywords, canonical URL, Twitter meta tags, OG meta tags, and relAlt tags.
 */
export const parseMetaString = (metaString) => {
  // Default return object
  const defaultReturn = {
    title: "",
    description: "",
    keywords: "",
    canonical: "",
    og: {
      title: "",
      description: "",
      image: "",
      url: "",
      type: "",
    },
    headAnalyticsData: {
      links: [],
      scriptSources: [],
      inLineScripts: [],
    },
  };

  // Handle null/undefined
  if (!metaString) {
    return defaultReturn;
  }

  // If metaString is already an object with the expected structure, return it
  // (batch API might return parsed object instead of HTML string)
  if (typeof metaString === "object" && !Array.isArray(metaString) && metaString !== null) {
    // Check if it's already a parsed object with expected structure
    if (metaString.title !== undefined || metaString.headAnalyticsData !== undefined) {
      // Return with defaults for missing fields
      return {
        title: metaString.title || "",
        description: metaString.description || "",
        keywords: metaString.keywords || "",
        canonical: metaString.canonical || "",
        og: {
          title: metaString.og?.title || "",
          description: metaString.og?.description || "",
          image: metaString.og?.image || "",
          url: metaString.og?.url || "",
          type: metaString.og?.type || "",
        },
        headAnalyticsData: {
          links: metaString.headAnalyticsData?.links || [],
          scriptSources: metaString.headAnalyticsData?.scriptSources || [],
          inLineScripts: metaString.headAnalyticsData?.inLineScripts || [],
        },
      };
    }

    // If it's an object but not in expected format, try to stringify it
    // (fallback for unexpected object formats)
    try {
      metaString = JSON.stringify(metaString);
    } catch (error) {
      console.warn("parseMetaString: Unable to convert object to string", error);
      return defaultReturn;
    }
  }

  // Ensure metaString is a string before calling .match()
  const metaStringValue = typeof metaString === "string" ? metaString : String(metaString);

  // Now safely call .match() on the string
  const titleMatch = metaStringValue.match(/<title>(.*?)<\/title>/);
  const descriptionMatch = metaStringValue.match(/<meta name="description" content="(.*?)"/);
  const keywordsMatch = metaStringValue.match(/<meta name="keywords" content="(.*?)"/);
  const canonicalMatch = metaStringValue.match(/<link rel="canonical" href="(.*?)"/);
  const ogTitleMatch = metaStringValue.match(/<meta property="og:title" content="(.*?)"/);
  const ogDescriptionMatch = metaStringValue.match(/<meta property="og:description" content="(.*?)"/);
  const ogImageMatch = metaStringValue.match(/<meta property="og:image" content="(.*?)"/);
  const ogUrlMatch = metaStringValue.match(/<meta property="og:url" content="(.*?)"/);
  const ogTypeMatch = metaStringValue.match(/<meta property="og:type" content="(.*?)"/);

  const scriptTags = metaStringValue.match(/<script[^>]*>.*?<\/script>/gs);
  const srcScripts = [];
  const inLineScripts = [];

  scriptTags?.forEach((scriptTag) => {
    // Look for src attributes strictly within the opening <script> tag
    const srcMatch = scriptTag.match(/<script[^>]*src=["']?([^"'>]+)["']?[^>]*>/);
    const hasAsync = /async/.test(scriptTag); // Check if async is present
    const hasDefer = /defer/.test(scriptTag); // Check if defer is present
    const typeMatch = scriptTag.match(/type=["'](.*?)["']/); // Look for type attribute
    const scriptType = typeMatch ? typeMatch[1] : "text/javascript";

    if (srcMatch) {
      // If a src attribute is found, it's an external script
      srcScripts.push({
        src: srcMatch[1],
        async: hasAsync,
        defer: hasDefer,
        type: scriptType,
      });
    } else {
      // If no src attribute, it's an inline script
      const inlineScript = scriptTag.replace(/<script[^>]*>|<\/script>/g, "").trim();

      // Ensure inline script is not empty or only whitespace
      if (inlineScript) {
        inLineScripts.push({
          content: inlineScript,
          type: scriptType,
        });
      }
    }
  });

  // Match all link tags with dns-prefetch, prefetch, preload, and preconnect
  const dnsPrefetchLinks = metaStringValue.match(
    /<link rel="(dns-prefetch|prefetch|preload|preconnect)" href="(.*?)"/g,
  );

  // Map the matched links into the desired object format
  const links = dnsPrefetchLinks
    ? dnsPrefetchLinks.map((link) => {
        // Extract the rel and href attributes from the matched link string
        const relMatch = link.match(/rel="(.*?)"/);
        const hrefMatch = link.match(/href="(.*?)"/);

        return {
          rel: relMatch ? relMatch[1] : "",
          href: hrefMatch ? hrefMatch[1] : "",
        };
      })
    : [];

  return {
    title: titleMatch ? titleMatch[1] : "",
    description: descriptionMatch ? descriptionMatch[1] : "",
    keywords: keywordsMatch ? keywordsMatch[1] : "",
    canonical: canonicalMatch ? canonicalMatch[1] : "",
    og: {
      title: ogTitleMatch ? ogTitleMatch[1] : "",
      description: ogDescriptionMatch ? ogDescriptionMatch[1] : "",
      image: ogImageMatch ? ogImageMatch[1] : "",
      url: ogUrlMatch ? ogUrlMatch[1] : "",
      type: ogTypeMatch ? ogTypeMatch[1] : "",
    },
    headAnalyticsData: {
      links,
      scriptSources: srcScripts,
      inLineScripts,
    },
  };
};

/**
 * Parses Twitter meta tags from a twitterTag string
 *
 * @param {string} twitterTag - The raw Twitter tag string containing meta tags
 * @param {Object} fallbacks - Fallback values for Twitter tags
 * @param {string} fallbacks.card - Default Twitter card type
 * @param {string} fallbacks.site - Default Twitter site
 * @param {string} fallbacks.image - Default Twitter image
 * @param {string} fallbacks.title - Default Twitter title
 * @param {string} fallbacks.description - Default Twitter description
 * @returns {Object} An object containing parsed Twitter meta tag values
 */
export const parseTwitterTags = (twitterTag, fallbacks = {}) => {
  const {
    card: defaultCard = "summary_large_image",
    site: defaultSite = "",
    image: defaultImage = "",
    title: defaultTitle = "",
    description: defaultDescription = "",
  } = fallbacks;

  if (!twitterTag) {
    return {
      card: defaultCard,
      site: defaultSite,
      image: defaultImage,
      title: defaultTitle,
      description: defaultDescription,
    };
  }

  try {
    const twitterCardMatch = twitterTag.match(/twitter:card["\s]*content=["']([^"']+)["']/i);
    const twitterSiteMatch = twitterTag.match(/twitter:site["\s]*content=["']([^"']+)["']/i);
    const twitterImageMatch = twitterTag.match(/twitter:image["\s]*content=["']([^"']+)["']/i);
    const twitterTitleMatch = twitterTag.match(/twitter:title["\s]*content=["']([^"']+)["']/i);
    const twitterDescMatch = twitterTag.match(/twitter:description["\s]*content=["']([^"']+)["']/i);

    return {
      card: twitterCardMatch ? twitterCardMatch[1] : defaultCard,
      site: twitterSiteMatch ? twitterSiteMatch[1] : defaultSite,
      image: twitterImageMatch ? twitterImageMatch[1] : defaultImage,
      title: twitterTitleMatch ? twitterTitleMatch[1] : defaultTitle,
      description: twitterDescMatch ? twitterDescMatch[1] : defaultDescription,
    };
  } catch (error) {
    console.log("Error parsing twitterTag", error);
    return {
      card: defaultCard,
      site: defaultSite,
      image: defaultImage,
      title: defaultTitle,
      description: defaultDescription,
    };
  }
};

/**
 * This method is used to check if cutoff timestamp exists
 *
 * @param {object} cutOffTimeStamp contains time.
 * @returns {boolean} returns true or false.
 */
export const isSameDayDeliveryAvailable = (cutOffTimeStamp) => {
  if (!cutOffTimeStamp) {
    return true;
  }
  const currentTime = new Date();
  const cuttoffSplit = cutOffTimeStamp && cutOffTimeStamp?.split(":");
  const cutoffTime = new Date();
  cutoffTime.setHours(cuttoffSplit[0]);
  cutoffTime.setMinutes(cuttoffSplit[1]);
  const isCutoffTimePassed = cutoffTime >= currentTime;
  return isCutoffTimePassed;
};

/**
 * Function to parse date from DD-MM-YYYY format
 * @param {*} dateString
 * @returns
 */
export function parseDate(dateString) {
  const [day, month, year] = dateString.split("-");
  return new Date(`${year}-${month}-${day}`);
}

/**
 * Retrieves the hash part of the current URL.
 *
 * @returns {string} The hash portion of the current URL (e.g., "#section1").
 *                   Returns an empty string if there is no hash or if executed in a non-browser environment.
 */
export const getHash = () => (typeof window !== "undefined" ? window.location.hash : "");

/**
 * Removes a specific hashtag from a URL.
 *
 * @param {string} url - The full URL containing the hashtags.
 * @param {string} hashtag - The specific hashtag to remove (e.g., "removeMe").
 * @returns {string} - The updated URL with the specified hashtag removed.
 * @throws {TypeError} - If the input URL is invalid.
 */
export function removeHashtagFromURL(hashtag) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const finalHash = hashtag.startsWith("#") ? hashtag.slice(1) : hashtag;
  try {
    const urlObj = new URL(url);
    const currentHash = urlObj.hash;

    if (currentHash.includes(finalHash)) {
      const updatedHash = currentHash
        .split("#") // Split the hash into parts
        .filter((tag) => tag !== finalHash) // Remove the specified hashtag
        .join("#"); // Join the remaining parts

      urlObj.hash = updatedHash ? `#${updatedHash}` : ""; // Update the hash
    }

    // Use history API to update the URL without reloading
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", urlObj.toString());
    }
  } catch (error) {
    console.error("Invalid URL:", error.message);
    return url;
  }
}

/**
 * method removes hash from the url
 *
 * @param {string} url - url string
 * @returns {string} final url
 */
export const removeHashFromURL = (url) => {
  const destinationURL = new URL(url);
  destinationURL.hash = "";
  return destinationURL.href;
};

/**
 * Triggers a click event on the button with the ID "bottom_drawer_btn".
 * This function is typically used to programmatically close a bottom drawer component.
 *
 * @function closeBottomDrawerFromParent
 */
export function closeBottomDrawerFromParent() {
  const closeBottomDrawer = document.getElementById("bottom_drawer_btn");
  if (closeBottomDrawer) {
    closeBottomDrawer.click();
  }
}

/**
 * Determines the page name based on certain conditions.
 *
 * @param {boolean} isHome - A flag indicating if the page is the home page.
 * @param {boolean} isPLP - A flag indicating if the page is a Product Listing Page (PLP).
 * @param {string} categoryId - The category ID to return if the page is a PLP.
 * @param {string} urlIdentifier - A URL identifier to return if the page is neither home nor PLP.
 *
 * @returns {string} The page name based on the input flags:
 * - Returns `cleverTapCommonWords.HOME` if `isHome` is true.
 * - Returns the `categoryId` if `isPLP` is true.
 * - Returns `urlIdentifier` if neither `isHome` nor `isPLP` are true.
 */
export const pageName = (isHome, isPLP, categoryId, urlIdentifier) => {
  if (isHome) {
    return cleverTapCommonWords.HOME;
  } else if (isPLP) {
    return categoryId;
  }
  return urlIdentifier;
};

/**
 * Toggles the visibility of an element by its ID.
 *
 * @param {string} id - The ID of the element to show or hide.
 * @param {boolean} isVisible - Whether to show (true) or hide (false) the element.
 */
export const toggleElementVisibility = (id, isVisible) => {
  const element = document.getElementById(id);
  if (!element) return;
  element.style.display = isVisible ? "block" : "none";
};

/**
 * This method initiates the lazy loading of all images on a page.
 * When the element is in the view port the data-src attributes or data-imageurl attributes
 * values are assigned to src attributes to load the images.
 */
export const lazyLoadImgs = () => {
  const elements = document.querySelectorAll("[data-imageurl],[data-src]");
  if (elements) {
    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const lazyImage = entry.target;
            if (lazyImage?.dataset?.imageurl) {
              if (lazyImage?.src !== lazyImage.dataset.imageurl) {
                lazyImage.src = lazyImage.dataset.imageurl;
              }
            } else if (lazyImage?.dataset?.src) {
              if (lazyImage.src !== lazyImage.dataset.src) {
                lazyImage.src = lazyImage.dataset.src;
              }
            }
          }
        });
      });
      elements.forEach((v) => imageObserver.observe(v));
    } else {
      elements.forEach((elem) => {
        const element = elem;
        if (element?.dataset?.src) element.src = elem.dataset.src;
        else if (element?.dataset?.imageurl) element.src = elem.dataset.imageurl;
      });
    }
  }
};
/**
 * This method is used to calculate percentage off product
 *
 * @param {number} listPrice list price.
 * @param {number} price price.
 * @returns {number} percentage
 */
export function calDiscountPercentage(listPrice, price) {
  const priceDifference = parseFloat(listPrice) - parseFloat(price);
  const percentage = (parseFloat(priceDifference) / parseFloat(listPrice)) * 100;
  return Math.floor(percentage);
}
/**
 * Determines the delivery type based on the primary category ID.
 *
 * @param {string} primaryCategoryId The primary category ID of the product.
 * @returns {string} The delivery type ('express' or 'courier').
 */
export const getProductDeliveryType = (primaryCategoryId) => {
  if (primaryCategoryId === productTypes.EXPRESS || primaryCategoryId === productTypes.PERSONALIZED) {
    return productTypes.EXPRESS;
  }
  return productTypes.COURIER;
};

/**
 * Smoothly scrolls the page to the element with the specified ID.
 *
 * @param {string} elementId - The ID of the target element to scroll to.
 * @throws {Error} Logs an error to the console if the element with the given ID is not found.
 *
 */
/**
 * Smoothly scrolls the page to the element with the specified ID, with an offset.
 *
 * @param {string} elementId - The ID of the target element to scroll to.
 * @param {number} [offset=0] - The offset to stop before the target element.
 */
export const onSmoothScrollToElement = (elementId, offset = 50) => {
  const targetElement = document.getElementById(elementId);
  if (targetElement) {
    // Calculate the position to scroll to with offset
    const offsetPosition = targetElement.getBoundingClientRect().top + window.scrollY - offset;

    // Smooth scroll to the calculated position
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  } else {
    console.error(`Element with id "${elementId}" not found.`);
  }
};

/**
 * Remove parameters from url.
 * @param {*} url
 * @param {*} parameter
 * @returns
 */
export const removeURLParameter = (url, parameter) => {
  const destinationURL = new URL(url);
  destinationURL.searchParams.delete(parameter);
  return destinationURL.href;
};

/** */
export function getMonthNumber(monthName) {
  const date = new Date(`${monthName} 1, 2000`); // Create a date object with the given month name
  return date.getMonth() + 1; // getMonth() returns 0-based month, so add 1
}

/**
 * @param {string} str input string
 * @returns {string} string converted to title case
 */
export const toTitleCase = (str) => {
  return str?.toLowerCase()?.replace(/\b(\w)/g, (char) => char?.toUpperCase());
};

/**
 * this function fetch api data where sameday category is available
 */
export const fetchSameDayCatalog = async () => {
  try {
    const res = await fetchContentFromCMSOnClient(GET_IS_SAME_DAY_CATALOG());
    return res || [];
    // setSameDay(myRes);
  } catch (error) {
    console.error("Error fetching same-day catalog:", error);
  }
};

/**
 * Detects the operating system of the user's device based on the `navigator.userAgent` string.
 *
 * @returns {string} The name of the operating system. Possible values are:
 * - "Windows"
 * - "Windows Phone"
 * - "Android"
 * - "iOS"
 * - "MacOS"
 * - "Linux"
 * - "Unknown" (if the OS cannot be determined)
 */
export const getOperatingSystem = () => {
  if (typeof window === "undefined") {
    return "Unknown";
  }
  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }
  if (/win/i.test(userAgent)) {
    return "Windows";
  }
  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }
  if (/mac/i.test(userAgent)) {
    return "MacOS";
  }
  if (/linux/i.test(userAgent)) {
    return "Linux";
  }
  return "Unknown";
};

/**
 * Detects the browser name based on the `navigator.userAgent` string.
 *
 * @returns {string} The name of the browser. Possible values are:
 * - "Chrome"
 * - "Firefox"
 * - "Safari"
 * - "Edge"
 * - "Opera"
 * - "Unknown" (if the browser cannot be determined)
 */
export const getBrowserName = () => {
  if (typeof window === "undefined") {
    return "Unknown";
  }
  const { userAgent } = window.navigator;

  if (/chrome|crios|crmo/i.test(userAgent) && !/edg/i.test(userAgent)) {
    return "Chrome";
  }
  if (/firefox|fxios/i.test(userAgent)) {
    return "Firefox";
  }
  if (/safari/i.test(userAgent) && !/chrome|crios|crmo|edg/i.test(userAgent)) {
    return "Safari";
  }
  if (/edg/i.test(userAgent)) {
    return "Edge";
  }
  if (/opera|opr\//i.test(userAgent)) {
    return "Opera";
  }
  return "Unknown";
};

/**
 * restrictAutoScrollingOnLoad this function restrict the browser to auto scroll where it left before load to that position
 * Also handles Next.js Link navigation to ensure users land at the top of the page, not where they left off
 */
export const restrictAutoScrollingOnLoad = () => {
  if (typeof window === "undefined") return;

  // Set browser history scroll restoration to manual (prevents browser from restoring scroll position)
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  let currentPathname = window.location.pathname;
  let isNavigating = false;

  /**
   * Handle link clicks to detect Next.js Link navigation
   * @param {Event} e - The click event
   */
  const handleLinkClick = (e) => {
    const link = e.target.closest("a[href]");
    // Only handle internal Next.js links
    if (link) {
      const href = link.getAttribute("href");
      if (href && href.startsWith("/") && !href.startsWith("http")) {
        isNavigating = true;
        // Scroll to top immediately when link is clicked (before navigation)
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    }
  };

  /**
   * Monitor pathname changes (Next.js App Router navigation)
   * Ensures page scrolls to top on route change
   */
  const checkRouteChange = () => {
    const newPathname = window.location.pathname;

    if (newPathname !== currentPathname) {
      // Route changed - ensure scroll is at top
      if (isNavigating) {
        // Use multiple attempts to ensure scroll to top works
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: "instant" });
          // Also set after a small delay to catch any late scroll attempts
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "instant" });
          }, 0);
        });
        isNavigating = false;
      }
      currentPathname = newPathname;
    }
  };

  /**
   * Handle popstate (back/forward navigation)
   * Ensures scroll stays at top even on browser navigation
   */
  const handlePopState = () => {
    // Ensure scroll restoration stays manual
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = "manual";
    }
    // Scroll to top on back/forward navigation
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    });
  };

  // Set up event listeners
  document.addEventListener("click", handleLinkClick, true);
  window.addEventListener("popstate", handlePopState);

  // Monitor for route changes (Next.js App Router)
  const routeCheckInterval = setInterval(checkRouteChange, 50);

  // Initial scroll to top to ensure we start at top
  window.scrollTo({ top: 0, behavior: "instant" });

  // Cleanup function
  return () => {
    document.removeEventListener("click", handleLinkClick, true);
    window.removeEventListener("popstate", handlePopState);
    clearInterval(routeCheckInterval);
  };
};

/**
 * Redirects the user to the checkout page.
 *
 */
export const redirectToCheckout = () => {
  window.location.href = `/${ROUTE_CONSTANTS.CHECKOUT}/${ROUTE_CONSTANTS.DELIVERY_ADDRESS}`;
};

/**
 * Sanitize and join the classes in single string.
 * @param  {...any} args
 * @returns
 */
export const formatClassNames = (...args) => [...args].filter(Boolean).join(" ");

/**
 *
 * @param {*} string that needs to be truncated with elipses
 * @param {*} noOfCharacter number of characters after which the truncation is needed
 */
export const generateTruncatedString = (string, noOfCharacter) => {
  if (!string) return "";

  if (string.length <= noOfCharacter) {
    return string;
  }

  return string.substring(0, noOfCharacter) + "...";
};

/**
 * Masks a phone number, keeping the first 2 and last 4 digits visible.
 * @param {string} phoneNumber - The phone number to mask.
 * @returns {string} Masked phone number, e.g. "99****7172".
 */
export const maskPhoneNumber = (phoneNumber) => {
  const digits = phoneNumber?.replace(/X/g, "*");
  return digits;
};

/**
 * Masks an email address, keeping the first 2 and last 3 characters of the local part visible.
 * @param {string} email - The email address to mask.
 * @returns {string} Masked email, e.g. "ma****786@gmail.com".
 */
export const maskEmail = (email) => {
  if (!email?.includes("@")) {
    return email || "";
  }

  const [localPart, domain] = email.split("@");
  if (localPart.length <= 5) {
    return `${localPart}@${domain}`;
  }

  return `${localPart.slice(0, 2)}****${localPart.slice(-3)}@${domain}`;
};

export const placeholderImageEncoded =
  "data:image/webp;base64,UklGRvwDAABXRUJQVlA4IPADAABwXQCdASpYAlgCPm02mkmkIyihIFToERANiWlu49f7Pm21F7NaZLD3EPnbAL2QQ8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo5RITCpIaDWqT0UtU2WCHiCo57Yh8QVHPbC0BOTT6CMzrgGvUaRkmIBG/h+2smtx5HriVPhYq15fntiHxBUc9sQ+IKjlG7CzBvSq11xO1THfrvcDM/0QeVBaA7lY422IfEFRz2xD4gqOe2JakLUALiIijsE4BQApF3rh2xD4gqOe2IfEFRz2xDsWJcZ15XoJG78Kx7LmV61isGTHPbEPiCo57Yh8QVHPbEzP7wbbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QVHPbEPiCo57Yh8QU9AA/v/l5gAAAAAAAAAAAAAAAAAAAAXyrpFJ4tAM6xhy7J8kI94IZLYAmI7zPXCkPmBH7R6uDH1HhWQ1oQ0a/u0y/NsaDytdoZHuQKqpapN7+4wCex/XTph7kSXG1rApZwsh49ZXH5LoTFU3+U+gdzGMZT4jtSYF4zvRvge6Pw58vjl3l0gzZDi3flTO8L3H7gJn57NJDmrnnQVJtQ6P2zzXXS5lbm5GYrAefxmohw1CRwknmQfVDh+hk3tBDTn2Y9bBgRkNGlLCJxGaUt0h6UPSdKnV+chgMRwHTShUUqiBfMRtuAcu1KStSgAAAAAAAAAAAAAAAAAAAAAAAAA=";

/**
 * Resolves a local icon path to the GCP static-assets CDN URL.
 * @param {string} path - Icon path (e.g. "/icons/cart.svg", "./icons/sms.svg")
 * @returns {string} Full CDN URL or original path if env is missing
 */
export function getIconUrl(path) {
  if (typeof path !== "string" || !path) {
    return path;
  }

  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("//")) {
    return path;
  }

  const iconsMatch = path.match(/^\.\/?icons\/(.+)$/) || path.match(/^\/?icons\/(.+)$/);
  if (!iconsMatch) {
    return path;
  }

  const domain = (process.env.NEXT_PUBLIC_ASSET_PREFIX || "").trim().replace(/\/$/, "");
  if (!domain) {
    return path;
  }

  return `${domain}/icons/web/${iconsMatch[1]}`;
}

/**
 * Builds a CSS `filter` that recolors a monochrome icon image to `color`
 * using an SVG `feFlood` + `SourceAlpha` composite.
 * @param {string} color - CSS color (e.g. `#0F172B`, `white`)
 * @returns {string}
 */
export function buildIconTintFilter(color) {
  if (!color) {
    return undefined;
  }
  const safeColor = String(color).replace(/[<>"']/g, "");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg"><filter id="f"><feFlood flood-color="${safeColor}" result="flood"/><feComposite in="flood" in2="SourceAlpha" operator="in"/></filter></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}#f")`;
}

/** */
export function getBaseUrl(url) {
  if (typeof url !== "string" || url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//")) {
    return url;
  }

  if (url.includes("/icons") || url.includes("image_placeholder-bb")) {
    return getIconUrl(url);
  }

  const domain = (process.env.NEXT_PUBLIC_ASSET_PREFIX || "").trim();
  if (!domain) return url;

  const normalizedDomain = domain.endsWith("/") ? domain.slice(0, -1) : domain;
  const normalizedUrl = url.startsWith("/") ? url.slice(1) : url;
  return `${normalizedDomain}/${normalizedUrl}`;
}

/**
 * Normalizes a pathname by removing locale prefixes, query parameters, hash, and trailing slashes
 * @param {string} pathname - The pathname to normalize
 * @returns {string} - The normalized pathname
 */
export const normalizePathname = (pathname) => {
  if (!pathname || typeof pathname !== "string") {
    return "/";
  }

  let normalized = pathname;

  // Remove locale prefix if present (e.g., /en/, /hi/)
  if (normalized.startsWith("/en/") || normalized.startsWith("/hi/")) {
    normalized = normalized.replace(/^\/(en|hi)/, "");
  }

  // Remove query parameters and hash
  normalized = normalized.split("?")[0].split("#")[0];

  // Remove trailing slashes for comparison (except root)
  if (normalized === "/" || normalized === "") {
    return "/";
  }

  return normalized.replace(/\/$/, "");
};

/**
 * Normalizes a target URL by extracting pathname from full URLs, removing query parameters, hash, and trailing slashes
 * @param {string} targetUrl - The target URL to normalize (can be full URL or relative path)
 * @returns {string} - The normalized pathname
 */
export const normalizeTargetUrl = (targetUrl) => {
  // Early return for invalid inputs
  if (!targetUrl || typeof targetUrl !== "string") {
    return "/";
  }

  // Store a safe copy to avoid any potential closure/hoisting issues
  const safeUrl = String(targetUrl).trim();
  if (!safeUrl) {
    return "/";
  }

  let targetPath = safeUrl;

  // Extract pathname from URL if it's a full URL
  try {
    if (safeUrl.startsWith("http://") || safeUrl.startsWith("https://")) {
      const url = new URL(safeUrl);
      targetPath = url.pathname || "/";
    } else if (!targetPath.startsWith("/")) {
      targetPath = `/${targetPath}`;
    }
  } catch (error) {
    // If URL parsing fails, use safeUrl as is (ensure it's still a valid string)
    if (typeof safeUrl === "string" && safeUrl.length > 0) {
      targetPath = safeUrl.startsWith("/") ? safeUrl : `/${safeUrl}`;
    } else {
      return "/";
    }
  }

  // Ensure targetPath is still valid before processing
  if (!targetPath || typeof targetPath !== "string") {
    return "/";
  }

  // Remove query parameters and hash from target
  targetPath = targetPath.split("?")[0].split("#")[0];

  // Remove trailing slashes for comparison (except root)
  if (targetPath === "/" || targetPath === "") {
    return "/";
  }

  return targetPath.replace(/\/$/, "");
};

/**
 * Compares two URLs to determine if they are the same
 * Normalizes both URLs before comparison (removes locale prefixes, query params, hash, trailing slashes)
 * @param {string} currentPathname - The current pathname
 * @param {string} targetUrl - The target URL to compare (can be full URL or relative path)
 * @returns {boolean} - True if URLs are the same, false otherwise
 */
export const areUrlsSame = (currentPathname, targetUrl) => {
  // Early return for invalid inputs to prevent errors during navigation
  if (currentPathname == null || targetUrl == null) {
    return false;
  }

  try {
    const normalizedCurrent = normalizePathname(currentPathname);
    const normalizedTarget = normalizeTargetUrl(targetUrl);

    return normalizedCurrent === normalizedTarget;
  } catch (error) {
    // If normalization fails, assume URLs are different
    console.warn("Error comparing URLs:", error);
    return false;
  }
};

/**
 * Convert 24 hour format to 12 hour format
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
export const convert24To12Format = (startTime, endTime) => {
  /**
   * Convert to 12 hour format
   * @param {*} time
   * @returns
   */
  const to12Hour = (time) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 || 12;

    return minute === 0 ? `${hour12}:00 ${period}` : `${hour12}:${minute} ${period}`;
  };

  if (!startTime || !endTime) {
    return "";
  }

  return `${to12Hour(startTime)} - ${to12Hour(endTime)}`;
};

/**
 * Returns current timestamp (DD/MM/YYYY HH:MM:SS)
 *
 * @returns {string} Current timestamp formatted as "DD/MM/YYYY HH:MM:SS".
 */
export const getTimeStamp = () => {
  const now = new Date();
  const date = now.toLocaleDateString("en-GB"); // DD/MM/YYYY
  const time = now.toLocaleTimeString("en-GB"); // HH:MM:SS
  return `${date} ${time}`;
};

/**
 * Parses Akamai Edgescape header into country and city.
 * @param {string} edgeHeader - e.g. "country_code=IN,city=NEWDELHI, timezone=GMT+5.50, ..."
 * @returns {{ countryCode: string, city: string }}
 */
export const parseEdgeHeaderLocation = (edgeHeader) => {
  if (!edgeHeader || typeof edgeHeader !== "string") {
    return { countryCode: "", city: "" };
  }

  const parsed = edgeHeader.split(",").reduce((acc, segment) => {
    const [key, ...valueParts] = segment.trim().split("=");
    if (key) {
      acc[key.trim()] = valueParts.join("=").trim();
    }
    return acc;
  }, {});

  return {
    countryCode: parsed.country_code || "",
    city: parsed.city || "",
  };
};

/**
 * Get user local currency based on edge header and currency list
 * @param {string} edgeHeader - Edge header
 * @param {array} currencyList - Currency list
 * @returns {string} - User local currency
 */
export const getUserLocalCurrency = (edgeHeader, currencyList) => {
  const countryCode = edgeHeader?.split(",")[0]?.split("=")[1];
  if (countryCode === "IN") return CURRENCY_ID;

  const userCountry = currencyList?.find((item) => item.currency.geoCode.includes(countryCode));
  return userCountry?.currency?.id || CURRENCY_ID;
};

/** */
export const buildPartnerAmountData = (partnerId, partnerPoints) => {
  if (!partnerId || partnerPoints === null || partnerPoints === undefined || partnerPoints === "") {
    return null;
  }
  return { [`${partnerId.toLowerCase()}_amount`]: partnerPoints };
};

// Cache-layer localStorage key of the accuracy-weightage API.
const LATEST_LOCATION_WEIGHTAGE_CACHE_KEY = "accuracyWeightage";

/**
 * Browser cookie value (no cookie util import, so API URL builders stay safe on the server).
 * @param {string} name
 * @returns {string}
 */
const readBrowserCookie = (name) => {
  const entry = document.cookie.split(";").find((item) => item.trim().startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.trim().slice(name.length + 1)) : "";
};

/**
 * Accuracy weightage map from the cache layer, or null when missing / expired.
 * @returns {Object|null}
 */
const getCachedAccuracyWeightage = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(LATEST_LOCATION_WEIGHTAGE_CACHE_KEY) || "null");
    return cached?.data && typeof cached.data === "object" && Date.now() <= cached.expiry ? cached.data : null;
  } catch {
    return null;
  }
};

/**
 * Accuracy level ("high" | "medium" | "low") of the latest Google place picked at the gate, or "".
 * @returns {string}
 */
const getLatestAccuracyLevel = () => {
  const placeTypes = readBrowserCookie(LAST_VISITED_PLACE_TYPES).split(",").filter(Boolean);
  // Level stored with the latest location: the gate place pick, or the selected saved address.
  const storedLevel = readBrowserCookie(LAST_VISITED_ACCURACY_LEVEL);
  // No place types (e.g. a saved address): its stored level, else the "accuracyType" cookie of the gate pick.
  if (!placeTypes.length && storedLevel) return storedLevel;
  const types = placeTypes.length ? placeTypes : [readBrowserCookie(LAST_VISITED_ACCURACY_TYPE)].filter(Boolean);
  if (!types.length) return "";
  const weightage = getCachedAccuracyWeightage();
  const levels = weightage ? types.map((type) => String(weightage[type] || "").toLowerCase()) : [];
  return ["high", "medium", "low"].find((level) => levels.includes(level)) || storedLevel;
};

let mapFormLocationGetter = null;

/**
 * Registers the reader of the map address form location kept in the store (set up by the store, so this file does
 * not import the store).
 * @param {Function} getter - Returns { lat, long, accuracyType } or null.
 */
export const registerMapFormLocationGetter = (getter) => {
  mapFormLocationGetter = getter;
};

/**
 * Serviceability API location params: the user's latest location (lat / long and the accuracy level when known)
 * replaces the given lat / long; the given values stay when no latest location is stored (and on the server).
 * @param {Object} [params] - Request params, may hold lat / long.
 * @returns {Object}
 */
export const withLatestLocation = ({ isSelectedAddressLocation = false, ...params } = {}) => {
  if (typeof document === "undefined") return params;
  // Cart item's selected address (e.g. checkout time slots): its own lat / long / accuracyType are sent as they are.
  if (isSelectedAddressLocation && Number(params.lat) && Number(params.long)) return params;
  // Location picked in the address map form (kept until saved, or cleared when the form closes / a newer choice).
  const formLocation = mapFormLocationGetter?.();
  if (Number(formLocation?.lat) && Number(formLocation?.long)) {
    return {
      ...params,
      lat: Number(formLocation.lat),
      long: Number(formLocation.long),
      ...(formLocation.accuracyType ? { accuracyType: formLocation.accuracyType } : {}),
    };
  }
  // Latest selected / added saved address, else the gate pick (a gate pick clears the address one).
  const [lat, long] = (readBrowserCookie(LAST_VISITED_ADDRESS_LAT_LONG) || readBrowserCookie(LAST_VISITED_LAT_LONG))
    .split(",")
    .map(Number);
  const accuracyType = getLatestAccuracyLevel();
  return {
    ...params,
    ...(lat && long ? { lat, long } : {}),
    ...(accuracyType ? { accuracyType } : {}),
  };
};
